import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import MyMouvOrganizerPanel from '../components/MyMouvOrganizerPanel';
import ApiaProPanel from '../components/ApiaProPanel';
import GreenApiaProPanel from '../components/GreenApiaProPanel';
import { useGreenIAStatus } from '../hooks/useGreenIAStatus';
import AppointmentsPanel from '../components/AppointmentsPanel';
import UnifiedFinancePanel from '../components/UnifiedFinancePanel';
import MyJobProPanel from '../components/MyJob/MyJobProPanel';
import { JOB_L as MYJOB_L } from '../components/MyJob/i18n';
import {
  TrendingUp, Plus, Eye, MousePointerClick, DollarSign,
  Calendar, CheckCircle, XCircle, Clock, Play, Image as ImageIcon,
  Trash2, Activity, BarChart3, ArrowLeft, Volume2, ShoppingBag, Bell, PhoneCall, Bot, Briefcase
} from 'lucide-react';
import Navigation from '../components/Navigation';
import SponsorshipInbox from '../components/SponsorshipInbox';
import OffersPanel from '../components/OffersPanel';
import AdsCommercialPanel from '../components/ads/AdsCommercialPanel';
import { acText } from '../components/ads/adsCommercialI18n';
import { useLanguage } from '../context/LanguageContext';

const tr = (t, key, fallback) => {
  const v = t(key);
  return !v || v === key ? fallback : v;
};

const ProSpace = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [ads, setAds] = useState([]);
  const greenIA = useGreenIAStatus();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') === 'commercial' ? 'commercial' : 'ads');

  useEffect(() => {
    // Allow PRO accounts and admins
    if (user && user.account_type !== 'PRO' && !user.is_admin) {
      toast.error(tr(t, 'ads.proOnly', 'Accès réservé aux comptes PRO'));
      navigate('/profile');
      return;
    }

    loadData();
    
    // Check for payment return
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      toast.success(tr(t, 'ads.paymentChecking', 'Vérification du paiement en cours...'));
      // Poll for payment confirmation
      setTimeout(() => loadData(), 2000);
    }
    
    const canceled = searchParams.get('canceled');
    if (canceled) {
      toast.error(tr(t, 'ads.paymentCanceled', 'Paiement annulé'));
    }
  }, [user, searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load data with individual error handling
      const [adsRes, analyticsRes] = await Promise.allSettled([
        axios.get('/api/pro/advertisements/my-ads'),
        axios.get('/api/pro/analytics')
      ]);
      
      if (adsRes.status === 'fulfilled') {
        setAds(adsRes.value.data);
      } else {
        console.error('Error loading ads:', adsRes.reason);
        setAds([]);
      }
      
      if (analyticsRes.status === 'fulfilled') {
        setAnalytics(analyticsRes.value.data);
      } else {
        console.error('Error loading analytics:', analyticsRes.reason);
        setAnalytics(null);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAd = async (adId) => {
    if (!window.confirm(tr(t, 'ads.confirmDelete', 'Voulez-vous vraiment supprimer cette publicité ?'))) return;
    
    try {
      await axios.delete(`/api/pro/advertisements/${adId}`);
      toast.success(tr(t, 'ads.adDeleted', 'Publicité supprimée'));
      loadData();
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error(tr(t, 'ads.deleteError', 'Erreur lors de la suppression'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center pb-20">
        <div className="text-white text-xl">{tr(t, 'ads.loading', 'Chargement...')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-20">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(user?.is_admin ? '/admin' : `/profile/${user?.id}`)}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>{tr(t, 'ads.back', 'Retour')}</span>
          </button>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent mb-2">
                {tr(t, 'ads.title', 'Destine Ads')}
              </h1>
              <p className="text-gray-400">{tr(t, 'ads.subtitle', 'Gérez vos publicités et suivez vos performances')}</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-full font-semibold transition-colors flex items-center space-x-2"
              data-testid="create-ad-button"
            >
              <Plus className="w-5 h-5" />
              <span>{tr(t, 'ads.createAd', 'Créer une publicité')}</span>
            </button>
          </div>

          {/* MyShop shortcut for PRO accounts AND admins */}
          {((user?.account_type || '').toUpperCase() === 'PRO' || user?.is_admin) && (
            <>
              <button
                onClick={() => navigate('/shop/manage')}
                className="w-full mb-4 p-4 rounded-2xl bg-gradient-to-r from-fuchsia-600/20 to-violet-600/20 border border-fuchsia-500/30 hover:from-fuchsia-600/30 hover:to-violet-600/30 transition-colors flex items-center justify-between"
                data-testid="prospace-shop-shortcut"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-fuchsia-500/30 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-fuchsia-300" />
                  </div>
                  <div className="text-left">
                    <p className="font-semibold">{tr(t, 'ads.myShop', 'Ma MyShop')}</p>
                    <p className="text-xs text-gray-400">{tr(t, 'ads.sellYourProductsServices', 'Vendez vos produits & services')}</p>
                  </div>
                </div>
                <ArrowLeft className="w-5 h-5 rotate-180 text-fuchsia-300" />
              </button>
              <ShopFinanceSnapshot />
              <PayoutInfoCard />
            </>
          )}

          {/* Analytics Cards */}
          {analytics && analytics.ads && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={<Activity className="w-6 h-6" />}
                label={tr(t, 'ads.activeAds', 'Publicités actives')}
                value={analytics.ads.active || 0}
                color="violet"
              />
              <StatCard
                icon={<Eye className="w-6 h-6" />}
                label={tr(t, 'ads.totalImpressions', 'Impressions totales')}
                value={(analytics.ads.total_impressions || 0).toLocaleString()}
                color="blue"
              />
              <StatCard
                icon={<MousePointerClick className="w-6 h-6" />}
                label={tr(t, 'ads.totalClicks', 'Clics totaux')}
                value={(analytics.ads.total_clicks || 0).toLocaleString()}
                color="green"
              />
              <StatCard
                icon={<BarChart3 className="w-6 h-6" />}
                label={tr(t, 'ads.ctr', 'Taux de clic (CTR)')}
                value={`${analytics.ads.total_impressions > 0 ? ((analytics.ads.total_clicks / analytics.ads.total_impressions) * 100).toFixed(2) : 0}%`}
                color="purple"
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('ads')}
            className={`px-4 py-2 rounded-full font-semibold transition-colors ${
              activeTab === 'ads'
                ? 'bg-violet-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {tr(t, 'ads.myAds', 'Mes publicités')} ({ads.length})
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            data-testid="pro-analytics-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors ${
              activeTab === 'analytics'
                ? 'bg-violet-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {tr(t, 'ads.analytics', 'Analytics')}
          </button>
          <button
            onClick={() => setActiveTab('commercial')}
            data-testid="pro-commercial-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === 'commercial'
                ? 'bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Bot className="w-4 h-4" />
            {acText(language).tab}
          </button>
          <button
            onClick={() => setActiveTab('sponsorship')}
            data-testid="pro-sponsorship-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors ${
              activeTab === 'sponsorship'
                ? 'bg-violet-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            {tr(t, 'ads.sponsorshipRequests', 'Demandes de sponsoring')}
          </button>
          <button
            onClick={() => setActiveTab('callbacks')}
            data-testid="pro-callbacks-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === 'callbacks'
                ? 'bg-violet-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Bell className="w-4 h-4" />
            {tr(t, 'ads.notifications', 'Notifications')}
          </button>
          <button
            onClick={() => setActiveTab('mymouv')}
            data-testid="pro-mymouv-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors ${
              activeTab === 'mymouv'
                ? 'bg-fuchsia-500 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            MyMouv
          </button>
          <button
            onClick={() => setActiveTab('apia')}
            data-testid="pro-apia-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === 'apia'
                ? 'bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Bot className="w-4 h-4" />
            {tr(t, 'apia.tabTitle', 'AP-IA')}
          </button>
          <button
            onClick={() => setActiveTab('appointments')}
            data-testid="pro-appointments-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === 'appointments'
                ? 'bg-gradient-to-r from-emerald-500 to-cyan-600 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Calendar className="w-4 h-4" />
            {tr(t, 'appointments.tabTitle', 'RDV')}
          </button>
          <button
            onClick={() => setActiveTab('myjob')}
            data-testid="pro-myjob-tab"
            className={`px-4 py-2 rounded-full font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === 'myjob'
                ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-black'
                : 'bg-white/5 text-gray-400 hover:bg-white/10'
            }`}
          >
            <Briefcase className="w-4 h-4" />
            {(MYJOB_L[language] || MYJOB_L.fr).jobPanel || 'MyJob'}
          </button>
        </div>

        {/* Content */}
        {activeTab === 'ads' ? (
          <AdsList ads={ads} onDelete={handleDeleteAd} onReload={loadData} />
        ) : activeTab === 'analytics' ? (
          <div className="space-y-4">
            <UnifiedFinancePanel />
            <AnalyticsView analytics={analytics} />
          </div>
        ) : activeTab === 'commercial' ? (
          <AdsCommercialPanel />
        ) : activeTab === 'callbacks' ? (
          <ServiceCallbacksPanel />
        ) : activeTab === 'mymouv' ? (
          <MyMouvOrganizerPanel />
        ) : activeTab === 'apia' ? (
          <div className="space-y-4">
            <ApiaProPanel />
            {greenIA.enabled && <GreenApiaProPanel />}
          </div>
        ) : activeTab === 'appointments' ? (
          <AppointmentsPanel />
        ) : activeTab === 'myjob' ? (
          <MyJobProPanel />
        ) : (
          <div className="space-y-6">
            <OffersPanel />
            <SponsorshipInbox />
          </div>
        )}
      </div>

      {/* Create Ad Modal */}
      {showCreateForm && (
        <CreateAdModal onClose={() => setShowCreateForm(false)} onSuccess={() => {
          setShowCreateForm(false);
          loadData();
        }} />
      )}

      <Navigation />
    </div>
  );
};

const StatCard = ({ icon, label, value, color }) => {
  const colorClasses = {
    violet: 'from-violet-500 to-purple-600',
    blue: 'from-blue-500 to-cyan-600',
    green: 'from-green-500 to-emerald-600',
    purple: 'from-purple-500 to-pink-600'
  };

  return (
    <div className="bg-white/5 rounded-2xl border border-white/10 p-4">
      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} mb-3`}>
        {icon}
      </div>
      <div className="text-2xl font-bold mb-1">{value}</div>
      <div className="text-sm text-gray-400">{label}</div>
    </div>
  );
};

const AdsList = ({ ads, onDelete, onReload }) => {
  const { t } = useLanguage();
  if (ads.length === 0) {
    return (
      <div className="text-center py-20">
        <div className="inline-flex p-6 bg-white/5 rounded-full mb-4">
          <TrendingUp className="w-12 h-12 text-gray-500" />
        </div>
        <h3 className="text-2xl font-semibold mb-2">{tr(t, 'ads.noAdsYet', 'Aucune publicité')}</h3>
        <p className="text-gray-400 mb-6">{tr(t, 'ads.noAdsHint', 'Créez votre première publicité pour commencer')}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {ads.map((ad) => (
        <AdCard key={ad.id} ad={ad} onDelete={onDelete} onReload={onReload} />
      ))}
    </div>
  );
};

const AdCard = ({ ad, onDelete, onReload }) => {
  const { t } = useLanguage();
  const [checkingPayment, setCheckingPayment] = useState(false);

  const getStatusInfo = (status) => {
    const statusMap = {
      'pending_payment': { label: tr(t, 'ads.statusPendingPayment', 'En attente de paiement'), color: 'yellow', icon: Clock },
      'approved': { label: tr(t, 'ads.statusApproved', 'Approuvée'), color: 'green', icon: CheckCircle },
      'active': { label: tr(t, 'ads.statusActive', 'Active'), color: 'green', icon: CheckCircle },
      'expired': { label: tr(t, 'ads.statusExpired', 'Expirée'), color: 'gray', icon: XCircle },
      'pending': { label: tr(t, 'ads.statusPending', 'En attente'), color: 'yellow', icon: Clock }
    };
    return statusMap[status] || { label: status, color: 'gray', icon: Clock };
  };

  const statusInfo = getStatusInfo(ad.status);
  const StatusIcon = statusInfo.icon;

  const handlePayment = async () => {
    try {
      const response = await axios.post(`/api/pro/advertisements/${ad.id}/checkout`);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(tr(t, 'ads.paymentCreateError', 'Erreur lors de la création du paiement'));
    }
  };

  const checkPaymentStatus = async () => {
    setCheckingPayment(true);
    try {
      const response = await axios.get(`/api/pro/advertisements/${ad.id}/payment-status`);
      if (response.data.payment_status === 'paid') {
        toast.success(tr(t, 'ads.paymentConfirmed', 'Paiement confirmé !'));
        onReload();
      } else {
        toast.info(tr(t, 'ads.paymentProcessing', 'Paiement en cours de traitement...'));
      }
    } catch (error) {
      console.error('Error checking payment:', error);
      toast.error(tr(t, 'ads.paymentCheckError', 'Erreur de vérification'));
    } finally {
      setCheckingPayment(false);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-violet-500/30 transition-all">
      {/* Preview */}
      <div className="relative aspect-video bg-gray-900">
        {ad.ad_type === 'video' ? (
          <video src={ad.content_url} className="w-full h-full object-cover" controls />
        ) : (
          <img src={ad.content_url} alt="Ad" className="w-full h-full object-cover" />
        )}
        <div className="absolute top-3 right-3 flex items-center space-x-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold bg-${statusInfo.color}-500/20 text-${statusInfo.color}-400 flex items-center space-x-1`}>
            <StatusIcon className="w-3 h-3" />
            <span>{statusInfo.label}</span>
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2 text-sm text-gray-400">
            {ad.ad_type === 'video' ? <Play className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
            <span>{ad.ad_type === 'video' ? tr(t, 'ads.adVideo', 'Vidéo') : tr(t, 'ads.adImage', 'Image')}</span>
            <span>•</span>
            <Calendar className="w-4 h-4" />
            <span>{ad.duration_days} {tr(t, 'ads.days', 'jours')}</span>
          </div>
          <div className="flex items-center space-x-2 text-sm font-semibold text-violet-400">
            <DollarSign className="w-4 h-4" />
            <span>{ad.price} EUR</span>
          </div>
        </div>

        {/* Zones */}
        {ad.zones && ad.zones.filter(z => z !== 'shortfilms').length > 0 && (
          <div className="mb-3">
            <div className="text-xs text-gray-500 mb-1">{tr(t, 'ads.broadcastZones', 'Zones de diffusion :')}</div>
            <div className="flex flex-wrap gap-1">
              {ad.zones.filter(z => z !== 'shortfilms').map((zone) => (
                <span key={zone} className="px-2 py-1 bg-white/5 rounded text-xs text-gray-300">
                  {zone}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-3 pt-3 border-t border-white/10">
          <div>
            <div className="text-xs text-gray-500">{tr(t, 'ads.impressions', 'Impressions')}</div>
            <div className="text-lg font-bold">{ad.impressions_count || 0}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">{tr(t, 'ads.clicks', 'Clics')}</div>
            <div className="text-lg font-bold">{ad.clicks_count || 0}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-2">
          {ad.status === 'pending_payment' && (
            <button
              onClick={handlePayment}
              className="flex-1 px-4 py-2 bg-violet-500 hover:bg-violet-600 rounded-full font-semibold transition-colors"
            >
              Payer (50 EUR)
            </button>
          )}
          {ad.payment_session_id && ad.payment_status !== 'paid' && (
            <button
              onClick={checkPaymentStatus}
              disabled={checkingPayment}
              className="flex-1 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-full font-semibold transition-colors disabled:opacity-50"
            >
              {checkingPayment ? tr(t, 'ads.checking', 'Vérification...') : tr(t, 'ads.checkPayment', 'Vérifier paiement')}
            </button>
          )}
          <button
            onClick={() => onDelete(ad.id)}
            className="p-2 text-red-400 hover:bg-red-500/10 rounded-full transition-colors"
            title={tr(t, 'ads.delete', 'Supprimer')}
          >
            <Trash2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const AnalyticsView = ({ analytics }) => {
  const { t } = useLanguage();
  if (!analytics || !analytics.ads) return null;

  const totalImpressions = analytics.ads.total_impressions || 0;
  const totalClicks = analytics.ads.total_clicks || 0;
  const ctr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : 0;
  const recentAds = analytics.recent_ads || [];

  return (
    <div className="space-y-6">
      <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <h3 className="text-xl font-bold mb-4">{tr(t, 'ads.performanceSummary', 'Résumé des performances')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-bold text-violet-400 mb-1">
              {totalImpressions.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">{tr(t, 'ads.totalImpressions', 'Impressions totales')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-400 mb-1">
              {totalClicks.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">{tr(t, 'ads.totalClicks', 'Clics totaux')}</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-purple-400 mb-1">
              {ctr}%
            </div>
            <div className="text-sm text-gray-400">{tr(t, 'ads.avgCtr', 'Taux de clic moyen')}</div>
          </div>
        </div>
      </div>

      <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
        <h3 className="text-xl font-bold mb-4">{tr(t, 'ads.details', 'Détails')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <div className="text-3xl font-bold text-violet-400 mb-1">
              {analytics.ads.total || 0}
            </div>
            <div className="text-sm text-gray-400">{tr(t, 'ads.adsCreated', 'Publicités créées')} ({analytics.ads.active || 0} {tr(t, 'ads.activeShort', 'actives')})</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-green-400 mb-1">
              {totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : 0}%
            </div>
            <div className="text-sm text-gray-400">{tr(t, 'ads.conversionRate', 'Taux de conversion')}</div>
          </div>
        </div>
      </div>

      {/* Ad performance breakdown */}
      {recentAds.length > 0 && (
        <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
          <h3 className="text-xl font-bold mb-4">{tr(t, 'ads.perAdPerformance', 'Performance par publicité')}</h3>
          <div className="space-y-3">
            {recentAds.map((ad) => (
              <div key={ad.id} className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                <div className="flex items-center space-x-3">
                  {ad.ad_type === 'video' ? <Play className="w-4 h-4 text-violet-400" /> : ad.ad_type === 'audio' ? <Volume2 className="w-4 h-4 text-pink-400" /> : <ImageIcon className="w-4 h-4 text-blue-400" />}
                  <span className="text-sm truncate max-w-48">{ad.title || ad.ad_type}</span>
                  <span className={`px-2 py-0.5 rounded text-xs ${ad.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {ad.status}
                  </span>
                </div>
                <div className="flex items-center space-x-4 text-sm text-gray-400">
                  <span>{ad.impressions_count || 0} imp.</span>
                  <span>{ad.clicks_count || 0} clics</span>
                  <span>{(ad.views_count || 0) + (ad.listens_count || 0)} vues</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CreateAdModal = ({ onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [adType, setAdType] = useState('video');
  const [file, setFile] = useState(null);
  const [zones, setZones] = useState(['feed']);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [externalLink, setExternalLink] = useState('');
  const [pricing, setPricing] = useState({
    video: { price_eur: 100, duration_days: 7 },
    audio: { price_eur: 50, duration_days: 7 },
    image: { price_eur: 50, duration_days: 7 },
  });

  useEffect(() => {
    axios.get('/api/ads/pricing').then(r => setPricing(p => ({ ...p, ...r.data }))).catch(() => {});
  }, []);

  const curPrice = pricing[adType]?.price_eur ?? (adType === 'video' ? 100 : 50);
  const curDuration = pricing[adType]?.duration_days ?? 7;
  const durationText = tr(t, 'ads.sevenDays', '7 jours').replace(/7/, String(curDuration));

  const availableZones = [
    { id: 'feed', label: tr(t, 'ads.feedVocaux', 'Feed (Vocaux)') },
    { id: 'stories', label: tr(t, 'ads.stories', 'Stories') }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(selectedFile);
  };

  const toggleZone = (zoneId) => {
    setZones(prev =>
      prev.includes(zoneId)
        ? prev.filter(z => z !== zoneId)
        : [...prev, zoneId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      toast.error(tr(t, 'ads.selectFile', 'Veuillez sélectionner un fichier'));
      return;
    }

    if (zones.length === 0) {
      toast.error(tr(t, 'ads.selectAtLeastOneZone', 'Sélectionnez au moins une zone de diffusion'));
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('content_file', file);
      formData.append('ad_type', adType);
      formData.append('zones', zones.join(','));
      if (externalLink) {
        formData.append('external_link', externalLink);
      }

      const response = await axios.post('/api/pro/advertisements/create', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(tr(t, 'ads.adCreated', 'Publicité créée ! Procédez au paiement.'));
      onSuccess();
    } catch (error) {
      console.error('Error creating ad:', error);
      toast.error(error.response?.data?.detail || tr(t, 'ads.createError', 'Erreur lors de la création'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-start justify-center p-4 overflow-y-auto" style={{ paddingTop: '40px' }}>
      <div className="bg-gray-900 border border-white/10 rounded-3xl max-w-2xl w-full mb-8">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold" data-testid="create-ad-modal-title">{tr(t, 'ads.createModalTitle', 'Créer une publicité')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type */}
            <div>
              <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.adTypeLabel', 'Type de publicité')}</label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setAdType('video')}
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    adType === 'video'
                      ? 'border-violet-500 bg-violet-500/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <Play className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">{tr(t, 'ads.adVideo', 'Vidéo')}</div>
                  <div className="text-xs text-gray-400">{tr(t, 'ads.videoMax', 'Max 30s, 30 MB')}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setAdType('image')}
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    adType === 'image'
                      ? 'border-violet-500 bg-violet-500/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <ImageIcon className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">{tr(t, 'ads.adImage', 'Image')}</div>
                  <div className="text-xs text-gray-400">{tr(t, 'ads.imageMax', 'Max 5 MB')}</div>
                </button>
                <button
                  type="button"
                  onClick={() => setAdType('audio')}
                  className={`p-4 rounded-xl border-2 transition-colors ${
                    adType === 'audio'
                      ? 'border-violet-500 bg-violet-500/10'
                      : 'border-white/10 hover:border-white/20'
                  }`}
                >
                  <Volume2 className="w-6 h-6 mx-auto mb-2" />
                  <div className="font-semibold">{tr(t, 'ads.adAudio', 'Audio')}</div>
                  <div className="text-xs text-gray-400">{tr(t, 'ads.audioMax', 'Max 30s, MP3')}</div>
                </button>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.file', 'Fichier')}</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept={
                  adType === 'video' 
                    ? 'video/mp4,video/webm,video/quicktime' 
                    : adType === 'audio'
                    ? 'audio/mpeg,audio/mp3'
                    : 'image/jpeg,image/png,image/webp'
                }
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-violet-500"
                required
              />
              {preview && (
                <div className="mt-3 rounded-xl overflow-hidden">
                  {adType === 'video' ? (
                    <video src={preview} controls className="w-full" />
                  ) : adType === 'audio' ? (
                    <div className="bg-white/5 p-4 rounded-xl">
                      <audio src={preview} controls className="w-full" />
                      <p className="text-xs text-gray-400 mt-2 text-center">
                        {tr(t, 'ads.maxDuration', 'Durée maximale : 30 secondes')}
                      </p>
                    </div>
                  ) : (
                    <img src={preview} alt="Preview" className="w-full" />
                  )}
                </div>
              )}
            </div>

            {/* Durée */}
            <div>
              <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.broadcastDuration', 'Durée de diffusion')}</label>
              <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white" data-testid="ad-duration-display">
                {durationText}
              </div>
            </div>

            {/* Lien externe */}
            <div>
              <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.externalLinkOptional', 'Lien externe (optionnel)')}</label>
              <input
                type="url"
                value={externalLink}
                onChange={(e) => setExternalLink(e.target.value)}
                placeholder={tr(t, 'ads.sitePlaceholder', 'https://votre-site.com')}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-violet-500"
              />
              <p className="text-xs text-gray-400 mt-1">
                {tr(t, 'ads.externalLinkHint', 'URL vers votre site, produit ou service')}
              </p>
            </div>

            {/* Zones */}
            <div>
              <label className="block text-sm font-semibold mb-2">{tr(t, 'ads.broadcastZones', 'Zones de diffusion').replace(/\s*:\s*$/, '')}</label>
              <div className="grid grid-cols-2 gap-2">
                {availableZones.map((zone) => (
                  <button
                    key={zone.id}
                    type="button"
                    onClick={() => toggleZone(zone.id)}
                    className={`p-3 rounded-xl border transition-colors text-left ${
                      zones.includes(zone.id)
                        ? 'border-violet-500 bg-violet-500/10'
                        : 'border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="text-sm font-semibold">{zone.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Prix */}
            <div className="bg-violet-500/10 border border-violet-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{tr(t, 'ads.adPrice', 'Prix de la publicité')}</div>
                  <div className="text-sm text-gray-400" data-testid="ad-price-detail">
                    {curPrice}€ · {durationText} ({adType === 'video' ? tr(t, 'ads.adVideo', 'Vidéo') : adType === 'audio' ? tr(t, 'ads.adAudio', 'Audio') : tr(t, 'ads.adImage', 'Image')})
                  </div>
                </div>
                <div className="text-2xl font-bold text-violet-400" data-testid="ad-price-amount">
                  {curPrice}€
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold transition-colors"
              >
                {tr(t, 'ads.cancel', 'Annuler')}
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-full font-semibold transition-colors disabled:opacity-50"
              >
                {uploading ? tr(t, 'ads.creating', 'Création...') : tr(t, 'ads.createAndPay', 'Créer et payer')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// === Coordonnées bancaires pour les paiements de la MyShop ===
const PayoutInfoCard = () => {
  const { t } = useLanguage();
  const [data, setData] = useState({ company_name: '', iban: '', bic: '' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get('/api/shop/payout-info').then(r => setData(r.data || { company_name: '', iban: '', bic: '' })).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put('/api/shop/payout-info', data);
      toast.success(tr(t, 'ads.saved', 'Coordonnées enregistrées'));
      setEditing(false);
    } catch { toast.error(tr(t, 'ads.genericError', 'Erreur')); }
    finally { setSaving(false); }
  };

  return (
    <div className="w-full mb-6 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30" data-testid="payout-info-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-emerald-200">💳 {tr(t, 'ads.payoutTitle', 'Coordonnées bancaires (MyShop)')}</h3>
        {!editing && (
          <button onClick={() => setEditing(true)} className="text-xs px-3 py-1 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30" data-testid="edit-payout-btn">
            {data.iban ? tr(t, 'ads.payoutModify', 'Modifier') : tr(t, 'ads.payoutFill', 'Renseigner')}
          </button>
        )}
      </div>
      <p className="text-xs text-gray-300 mb-3">
        {tr(t, 'ads.payoutHint', "Ces informations sont nécessaires pour recevoir les paiements de vos ventes (commission Destine : 5 %). Elles ne sont visibles que par vous et l'équipe admin.")}
      </p>
      {!editing ? (
        <div className="space-y-1 text-xs">
          <div><span className="text-gray-400">{tr(t, 'ads.companyName', 'Raison sociale')} :</span> <span className="font-semibold text-white">{data.company_name || '—'}</span></div>
          <div><span className="text-gray-400">{tr(t, 'ads.iban', 'IBAN')} :</span> <span className="font-mono text-white">{data.iban || '—'}</span></div>
          <div><span className="text-gray-400">{tr(t, 'ads.bic', 'BIC')} :</span> <span className="font-mono text-white">{data.bic || '—'}</span></div>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            value={data.company_name || ''}
            onChange={e => setData(d => ({ ...d, company_name: e.target.value }))}
            placeholder={tr(t, 'ads.companyPlaceholder', 'Raison sociale (ex. SARL Boutique XYZ)')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm"
            data-testid="payout-company-input"
          />
          <input
            value={data.iban || ''}
            onChange={e => setData(d => ({ ...d, iban: e.target.value.toUpperCase() }))}
            placeholder={tr(t, 'ads.iban', 'IBAN')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono"
            data-testid="payout-iban-input"
          />
          <input
            value={data.bic || ''}
            onChange={e => setData(d => ({ ...d, bic: e.target.value.toUpperCase() }))}
            placeholder={tr(t, 'ads.bicPlaceholder', 'BIC / SWIFT')}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm font-mono"
            data-testid="payout-bic-input"
          />
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} className="flex-1 py-2 rounded-full bg-white/10 hover:bg-white/20 text-xs">{tr(t, 'ads.cancel', 'Annuler')}</button>
            <button onClick={save} disabled={saving} className="flex-1 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-xs font-semibold disabled:opacity-50" data-testid="save-payout-btn">
              {saving ? tr(t, 'ads.saving', 'Enregistrement…') : tr(t, 'ads.save', 'Enregistrer')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProSpace;

// ===== MyShop finance snapshot (PRO only) =====
const ShopFinanceSnapshot = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    axios.get('/api/shop/me/finance-snapshot')
      .then(r => setSnap(r.data || null))
      .catch(() => setSnap(null));
  }, []);
  if (!snap || (snap.shops_count || 0) === 0) return null;
  const fmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: (snap.currency || 'eur').toUpperCase() });
  return (
    <div className="w-full mb-4 p-4 rounded-2xl bg-gradient-to-br from-fuchsia-600/15 to-violet-700/10 border border-fuchsia-500/30" data-testid="prospace-shop-finance">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-fuchsia-200 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" /> {tr(t, 'ads.shopAccounting', 'Comptabilité MyShop')}
        </h3>
        <button onClick={() => navigate('/shop/manage')} className="text-[11px] px-3 py-1 rounded-full bg-fuchsia-500/20 hover:bg-fuchsia-500/30" data-testid="shop-finance-detail-btn">
          {tr(t, 'ads.detailsBtn', 'Détails')}
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className="p-2 rounded-xl bg-white/5 border border-white/10">
          <p className="text-[10px] text-gray-400">{tr(t, 'ads.gross', 'Brut')}</p>
          <p className="text-sm font-bold text-white" data-testid="shop-finance-gross">{fmt.format((snap.gross_cents || 0) / 100)}</p>
        </div>
        <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/30">
          <p className="text-[10px] text-amber-200/80">{tr(t, 'ads.commissionPct', 'Commission')} ({snap.commission_percent || 5}%)</p>
          <p className="text-sm font-bold text-amber-200" data-testid="shop-finance-commission">−{fmt.format((snap.commission_cents || 0) / 100)}</p>
        </div>
        <div className="p-2 rounded-xl bg-emerald-500/15 border border-emerald-500/40">
          <p className="text-[10px] text-emerald-200/80">{tr(t, 'ads.netReceive', 'Net à recevoir')}</p>
          <p className="text-sm font-bold text-emerald-200" data-testid="shop-finance-net">{fmt.format((snap.net_cents || 0) / 100)}</p>
        </div>
      </div>
      <p className="text-[11px] text-gray-400">
        {snap.sales_count || 0} {tr(t, 'ads.salesCount', 'vente(s)')} · {snap.shops_count || 0} {tr(t, 'ads.shopsCount', 'boutique(s)')}. {tr(t, 'ads.netExplain', "Net = chiffre d'affaires moins la commission Destine.")}
      </p>
    </div>
  );
};

// ===== Service callbacks panel (PRO only — "Notifications" tab) =====
// Aggregates every "Demander un rappel" submitted on the user's shops so the
// seller can quickly call the prospect back. Status can be flipped to
// "called"/"done" to keep the queue clean.
const ServiceCallbacksPanel = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/shop/me/service-callbacks');
      setItems(r.data?.callbacks || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const mark = async (id, status) => {
    try {
      await axios.patch(`/api/shop/service-callbacks/${id}`, { status });
      toast.success(tr(t, 'ads.updated', 'Mis à jour'));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || tr(t, 'ads.genericError', 'Erreur')); }
  };

  if (loading) return <p className="text-center text-gray-400 py-8">{tr(t, 'ads.loading', 'Chargement…')}</p>;
  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="callbacks-empty">
        <Bell className="w-10 h-10 mx-auto mb-2 text-gray-500" />
        <p className="text-sm">{tr(t, 'ads.noCallbacks', 'Aucune demande de rappel pour le moment.')}</p>
        <p className="text-xs mt-1">{tr(t, 'ads.noCallbacksHint', 'Les demandes envoyées par les acheteurs sur vos services apparaîtront ici.')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="callbacks-list">
      {items.map(c => {
        const isOpen = (c.status || 'open') === 'open';
        const dt = (() => { try { return new Date(c.created_at).toLocaleString(); } catch { return c.created_at; } })();
        return (
          <div key={c.id} className="p-3 rounded-2xl bg-white/5 border border-white/10" data-testid={`callback-${c.id}`}>
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate text-white">{c.product_title}</p>
                <p className="text-[11px] text-gray-400 truncate">{c.shop_name} · {dt}</p>
              </div>
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                isOpen ? 'bg-fuchsia-500/20 text-fuchsia-200'
                       : c.status === 'called' ? 'bg-emerald-500/20 text-emerald-200'
                       : 'bg-gray-500/20 text-gray-300'
              }`}>{c.status || 'open'}</span>
            </div>
            <div className="text-sm text-white">
              <span className="text-gray-400">{tr(t, 'ads.client', 'Client')} :</span> <span className="font-semibold">{c.buyer_name}</span>
            </div>
            <a
              href={`tel:${(c.buyer_phone || '').replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 mt-1 px-3 py-1.5 rounded-full bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-200 text-xs font-semibold"
              data-testid={`callback-call-${c.id}`}
            >
              <PhoneCall className="w-3.5 h-3.5" /> {c.buyer_phone}
            </a>
            {c.message && <p className="text-xs text-gray-300 mt-2 whitespace-pre-line">{c.message}</p>}
            <div className="flex gap-1.5 mt-2">
              {isOpen && (
                <button onClick={() => mark(c.id, 'called')} className="flex-1 py-1.5 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-[11px] font-semibold" data-testid={`callback-mark-called-${c.id}`}>
                  {tr(t, 'ads.markCalled', 'Marquer comme rappelé')}
                </button>
              )}
              {c.status !== 'archived' && (
                <button onClick={() => mark(c.id, 'archived')} className="flex-1 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-[11px]" data-testid={`callback-archive-${c.id}`}>
                  {tr(t, 'ads.archive', 'Archiver')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
